import { Sequencer, WorkletSynthesizer } from "spessasynth_lib";
import * as timing from "./timing.js";

// ─── State ──────────────────────────────────────────────
let audioCtx = null;
let synth = null;
let seq = null;
let manifest = null;
let currentPiece = null;
let isPlaying = false;
let tempoBpm = 80;
let loopStart = 1;
let loopEnd = 1;
let originalTempoBpm = 120;
// Measure grid of the CURRENT piece (timing.buildMeasureTable): where every bar
// starts and how many beats it has. Read from the MIDI's own time-signature
// events, so a piece that changes meter still lands on the right barlines.
let measureTable = null;
// Which measure range the loaded sequencer holds, and how the excerpt's timeline
// maps back to piece measures. Null until a piece is loaded.
let loopBuild = null;
let positionPollInterval = null;
// Pause between loop iterations, in ms. The loop boundary does not have to be
// rhythmic, so this is deliberately generous: it gives the player a clear break
// and the asynchronous seek room to settle before the next count-off.
const LOOP_RESTART_PAUSE_MS = 500;
// Play is async now (a moved loop start rebuilds the sequence first). These guard
// against a double Play and against a Stop landing mid-rebuild.
let startInProgress = false;
let playRequested = false;
let loopRestartTimeout = null;

const PASSWORD_KEY = "clefai_password";
const LOG_BUFFER_KEY = "clefai_log_buffer";
const SESSION_ID_KEY = "clefai_session_id";

// Persistent random ID per browser (survives restarts, distinguishes users)
function getSessionId() {
  let id = localStorage.getItem(SESSION_ID_KEY);
  if (!id) {
    id = Math.random().toString(36).slice(2, 8);
    localStorage.setItem(SESSION_ID_KEY, id);
  }
  return id;
}
const TUNING_SEMITONES = 0.0785; // 440 Hz → 442 Hz (7.85 cents)
const METRO_CHANNEL = 9; // MIDI channel 10 (0-based = 9) is percussion
// The count-off gets its OWN percussion channel, which is never muted. That way
// "metronome off" needs no switching at the loop start: channel 9 is simply muted
// from the outset and the count-in clicks come through here. Nothing is switched
// between the count-in "1" and the end of the loop — see timing.js, "Worklet
// round-trip margins", for why that matters. Instrument tracks occupy channels
// 0..n-1, so a manifest voice must sit on channel 1-9; check-midi.js enforces it.
const COUNT_IN_CHANNEL = 10;
const CC_MAIN_VOLUME = 7; // MIDI CC 7 = channel volume (live metronome loudness)
const METRO_VOLUME_KEY = "clefai_metro_volume";
// Slider default. The older files in midi/ set CC 7 = 101 once per voice at the
// start of the piece and never touch it again; the LilyPond-generated ones
// (Weihnachts-Chaconne, Gluck-Melodie) contain no CC 7 at all. Either way
// applyTrackVolumes() writes this value per channel on load and on every start,
// so 101 is what the mix sounds like until the player moves a slider. No piece
// uses CC 7 for dynamics, so locking it per channel cannot flatten a crescendo.
const DEFAULT_TRACK_VOLUME = 101;
// Four distinct timbres so the counting structure is clearly audible, and loud
// enough to cut through a forte passage. Tweak these by ear — they are all just
// GM percussion note + velocity (0–127). Subdivision loudness also scales with
// the live metronome-volume slider (CC 7).
const METRO_DOWNBEAT = 76;          // GM Hi Wood Block — beat "1", must stand out
const METRO_DOWNBEAT_VELOCITY = 127;
const METRO_BEAT = 77;              // GM Low Wood Block — main beats 2,3,4
const METRO_BEAT_VELOCITY = 112;
const METRO_EIGHTH = 56;            // GM Cowbell — the "und" (half-beat), short + cutting, no ring
const METRO_EIGHTH_VELOCITY = 88;
const METRO_SIXTEENTH = 42;         // GM Closed Hi-Hat — the "e"/"a" (quarter positions)
const METRO_SIXTEENTH_VELOCITY = 127;
const METRO_TRIPLET = 56;           // GM Cowbell — the two triplet in-between notes
const METRO_TRIPLET_VELOCITY = 104;

// Current metronome subdivision mode (session-only, resets to "quarter" on reload).
// "quarter" = status quo (only the main beats).
let metronomeSubdivision = "quarter"; // "quarter" | "eighth" | "sixteenth" | "triplet"

// Metronome channel volume (MIDI CC 7, 0–127). Persisted across sessions so the
// player's chosen loudness sticks. Applied live and re-applied after each load.
let metronomeVolume = (() => {
  const stored = parseInt(localStorage.getItem(METRO_VOLUME_KEY), 10);
  return Number.isFinite(stored) && stored >= 0 && stored <= 127 ? stored : 100;
})();

// Sub-click positions within one beat (fraction in [0,1)) and their sound,
// excluding the beat itself (fraction 0, handled as downbeat/beat).
function getSubClicks(mode) {
  switch (mode) {
    case "eighth":
      return [{ frac: 0.5, note: METRO_EIGHTH, vel: METRO_EIGHTH_VELOCITY }];
    case "sixteenth":
      return [
        { frac: 0.25, note: METRO_SIXTEENTH, vel: METRO_SIXTEENTH_VELOCITY },
        { frac: 0.5, note: METRO_EIGHTH, vel: METRO_EIGHTH_VELOCITY },
        { frac: 0.75, note: METRO_SIXTEENTH, vel: METRO_SIXTEENTH_VELOCITY },
      ];
    case "triplet":
      return [
        { frac: 1 / 3, note: METRO_TRIPLET, vel: METRO_TRIPLET_VELOCITY },
        { frac: 2 / 3, note: METRO_TRIPLET, vel: METRO_TRIPLET_VELOCITY },
      ];
    default:
      return [];
  }
}

let currentMidiBuffer = null; // Raw (un-injected) MIDI of current piece, cached for re-init

// ─── DOM Elements ───────────────────────────────────────
const $ = (id) => document.getElementById(id);
const screenLogin = $("screen-login");
const screenPlayer = $("screen-player");
const btnLogin = $("btn-login");
const inputPassword = $("input-password");
const passwordError = $("password-error");
const loadingIndicator = $("loading-indicator");
const errorMessage = $("error-message");
const playerControls = $("player-controls");
const selectPiece = $("select-piece");
const trackCheckboxes = $("track-checkboxes");
const btnAllOn = $("btn-all-on");
const inputLoopStart = $("input-loop-start");
const inputLoopEnd = $("input-loop-end");
const totalMeasuresLabel = $("total-measures-label");
const btnTempoDown = $("btn-tempo-down");
const btnTempoUp = $("btn-tempo-up");
const tempoDisplay = $("tempo-display");
const checkMetronome = $("check-metronome");
const btnMetronomeSettings = $("btn-metronome-settings");
const metronomeSettings = $("metronome-settings");
const inputMetronomeVolume = $("input-metronome-volume");
const beatDots = $("beat-dots");
const measureDisplay = $("measure-display");
const btnPlay = $("btn-play");
const btnStop = $("btn-stop");

// ─── Screen Navigation ──────────────────────────────────
function showScreen(screen) {
  document.querySelectorAll(".screen").forEach((s) => s.classList.remove("active"));
  screen.classList.add("active");
}

// ─── Logging ────────────────────────────────────────────
function logEvent(action, extra = {}) {
  const event = {
    sid: getSessionId(),
    piece: currentPiece?.id || null,
    action,
    tempoBpm,
    loopStart,
    loopEnd,
    activeTracks: getActiveTracks(),
    ...extra,
  };

  if (navigator.onLine) {
    flushLogBuffer();
    fetch("/api/log", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(event),
    }).catch(() => bufferLogEvent(event));
  } else {
    bufferLogEvent(event);
  }
}

function bufferLogEvent(event) {
  const buffer = JSON.parse(localStorage.getItem(LOG_BUFFER_KEY) || "[]");
  buffer.push({ ...event, ts: new Date().toISOString() });
  localStorage.setItem(LOG_BUFFER_KEY, JSON.stringify(buffer));
}

function flushLogBuffer() {
  const buffer = JSON.parse(localStorage.getItem(LOG_BUFFER_KEY) || "[]");
  if (buffer.length === 0) return;
  localStorage.removeItem(LOG_BUFFER_KEY);
  fetch("/api/log/batch", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ events: buffer }),
  }).catch(() => {
    // Re-buffer if send fails
    const existing = JSON.parse(localStorage.getItem(LOG_BUFFER_KEY) || "[]");
    localStorage.setItem(LOG_BUFFER_KEY, JSON.stringify([...buffer, ...existing]));
  });
}

window.addEventListener("online", flushLogBuffer);

// ─── Auth ───────────────────────────────────────────────
function getPassword() {
  return localStorage.getItem(PASSWORD_KEY) || "";
}

function authHeaders() {
  return { "x-clefai-auth": getPassword() };
}

async function testAuth() {
  try {
    const res = await fetch("/manifest", { headers: authHeaders() });
    return res.ok;
  } catch {
    return false;
  }
}

// ─── Debug (hidden by default, toggle with ?debug in URL) ─
const debugEnabled = new URLSearchParams(window.location.search).has("debug");
function debugLog(msg) {
  console.log(msg);
  if (!debugEnabled) return;
  const el = document.getElementById("debug-log");
  if (el) {
    el.textContent += msg + "\n";
    el.classList.remove("hidden");
  }
}

// ─── Audio Init ─────────────────────────────────────────
async function initAudio() {
  audioCtx = new AudioContext();
  await audioCtx.resume();
  await audioCtx.audioWorklet.addModule("/spessasynth_processor.min.js");
  synth = new WorkletSynthesizer(audioCtx);
  synth.connect(audioCtx.destination);
  debugLog(`Audio init OK. State: ${audioCtx.state}, sampleRate: ${audioCtx.sampleRate}`);
}

// ─── Load SoundFont ─────────────────────────────────────
const loadingLabel = $("loading-label");
const loadingBar = $("loading-bar");
const loadingBarFill = $("loading-bar-fill");
const loadingDetail = $("loading-detail");

const formatMB = (bytes) => (bytes / 1048576).toFixed(1).replace(".", ",");

// percent = null → unbestimmt (Leiste läuft hin und her, kein aria-valuenow).
function setLoadingProgress(label, percent, detail = "") {
  loadingLabel.textContent = label;
  loadingDetail.textContent = detail;
  if (percent === null) {
    loadingBar.classList.add("indeterminate");
    loadingBar.removeAttribute("aria-valuenow");
    loadingBarFill.style.width = "";
  } else {
    loadingBar.classList.remove("indeterminate");
    loadingBar.setAttribute("aria-valuenow", String(Math.round(percent)));
    loadingBarFill.style.width = `${percent}%`;
  }
}

// Liest den Body stückweise, damit die Leiste mitläuft. Content-Length fehlt
// oder stimmt nicht, wenn der Server komprimiert (dann zählt der Header die
// komprimierten Bytes, gelesen werden die entpackten) — in beiden Fällen
// bleibt die Leiste unbestimmt und nur die MB-Zahl läuft hoch.
async function fetchWithProgress(response) {
  const encoded = response.headers.get("content-encoding");
  const total = encoded ? 0 : Number(response.headers.get("content-length")) || 0;
  if (!response.body) return response.arrayBuffer();

  const reader = response.body.getReader();
  const chunks = [];
  let received = 0;
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
    received += value.length;
    if (total && received <= total) {
      setLoadingProgress("Lade SoundFont…", (received / total) * 100,
        `${formatMB(received)} / ${formatMB(total)} MB`);
    } else {
      setLoadingProgress("Lade SoundFont…", null, `${formatMB(received)} MB`);
    }
  }

  const buffer = new Uint8Array(received);
  let offset = 0;
  for (const chunk of chunks) {
    buffer.set(chunk, offset);
    offset += chunk.length;
  }
  return buffer.buffer;
}

async function loadSoundFont() {
  loadingIndicator.classList.remove("hidden");
  setLoadingProgress("Lade SoundFont…", null);
  try {
    const sfResponse = await fetch("/sf2/gm-lite.sf2");
    if (!sfResponse.ok) throw new Error("SoundFont nicht gefunden");
    const sfBuffer = await fetchWithProgress(sfResponse);
    // Das Parsen der Samples dauert nach dem Download noch spürbar — ohne
    // eigenen Hinweis stünde die Leiste so lange bei 100 %.
    setLoadingProgress("Bereite Klänge vor…", null);
    await synth.soundBankManager.addSoundBank(sfBuffer, "main");
    await synth.isReady;
    debugLog("SoundFont loaded and ready");
  } catch (err) {
    showError("SoundFont konnte nicht geladen werden. Bitte Seite neu laden.");
    throw err;
  } finally {
    loadingIndicator.classList.add("hidden");
  }
}

// ─── Load Manifest ──────────────────────────────────────
async function loadManifest() {
  const res = await fetch("/manifest", { headers: authHeaders() });
  if (!res.ok) throw new Error("Manifest nicht ladbar");
  manifest = await res.json();
}

// ─── Enter Player ───────────────────────────────────────
async function enterPlayer() {
  showScreen(screenPlayer);
  loadingIndicator.classList.remove("hidden");
  playerControls.classList.add("hidden");

  try {
    await loadSoundFont();
    await loadManifest();
    populatePieceSelector();
    playerControls.classList.remove("hidden");
    if (manifest.pieces.length > 0) {
      await selectPieceByIndex(0);
    }
  } catch (err) {
    showError(err.message || "Ein Fehler ist aufgetreten. Bitte Seite neu laden.");
  }
}

// ─── Piece Selection ────────────────────────────────────
function populatePieceSelector() {
  selectPiece.innerHTML = "";
  for (const piece of manifest.pieces) {
    const opt = document.createElement("option");
    opt.value = piece.id;
    opt.textContent = piece.title;
    selectPiece.appendChild(opt);
  }
}

// Build a fresh sequencer holding measures `fromMeasure..toMeasure` of `piece`
// and nothing else (buildLoopMidi), with the metronome track for the CURRENT
// subdivision mode, program overrides, 442 Hz tuning and the metronome mute
// state. Deliberately does NOT touch tempo or track checkboxes — callers decide
// whether to reset those (piece selection resets them; a re-init preserves them).
async function loadSequenceFromBuffer(piece, midiBuffer, fromMeasure = 1, toMeasure = piece.totalMeasures) {
  // Clean up old sequencer before creating new one
  if (seq) {
    try { seq.pause(); } catch {}
    try { seq.destroy(); } catch {}
    seq = null;
  }

  // Unlock all channels before loading (preset lock and the per-voice volume
  // lock set by applyTrackVolumes — the fresh song must start from a clean slate)
  for (let ch = 0; ch < 16; ch++) {
    try { synth.lockController(ch, -1, false); } catch {}
    try { synth.lockController(ch, CC_MAIN_VOLUME, false); } catch {}
  }

  // Read tempo and the measure grid from the raw MIDI, before cutting anything.
  originalTempoBpm = parseMidiTempo(midiBuffer);
  measureTable = timing.buildMeasureTable(midiBuffer);

  const from = Math.max(1, Math.min(fromMeasure, measureTable.totalMeasures));
  const to = Math.max(from, Math.min(toMeasure, measureTable.totalMeasures));
  const layout = timing.excerptLayout(measureTable, from, to);
  const loopMidi = buildLoopMidi(midiBuffer, measureTable, layout, from, to);
  loopBuild = {
    fromMeasure: from,
    toMeasure: to,
    fromTick: layout.fromTick,
    countInTicks: layout.countInTicks,
    musicStart: layout.musicStart,
  };

  seq = new Sequencer(synth);
  // The excerpt opens with a count-off click at tick 0, so there is nothing to
  // skip; say so explicitly rather than relying on that staying true.
  try { seq.skipToFirstNoteOn = false; } catch {}

  // Load MIDI and wait until the Worker has fully parsed it.
  // songChange fires before parsing is complete, so we poll seq.duration
  // which becomes > 0 only after the MIDI is fully parsed.
  seq.loadNewSongList([{ binary: loopMidi }]);
  await new Promise((resolve) => {
    let attempts = 0;
    const check = () => {
      attempts++;
      if (seq.duration > 0 || attempts > 50) {
        debugLog(`MIDI ready after ${attempts} polls. duration=${seq.duration.toFixed(2)}s, seq.currentTempo=${seq.currentTempo}`);
        resolve();
      } else {
        setTimeout(check, 50);
      }
    };
    setTimeout(check, 50);
  });

  seq.loop = false;
  // End of excerpt = end of loop. The restart hangs off the sequencer's own
  // report instead of a polled position (see onExcerptEnded).
  seq.eventHandler.addEvent("songEnded", "loop-restart", onExcerptEnded);

  // Override instrument programs from manifest (MIDIs often have wrong programs)
  // Must set program AND lock it, otherwise the sequencer's own program change
  // events in the MIDI file will overwrite our setting.
  for (const t of piece.tracks) {
    if (t.program !== undefined) {
      const channel = t.track - 1; // MIDI channels are 0-based
      if (channel >= 0 && channel < 16) {
        synth.programChange(channel, t.program);
        synth.lockController(channel, -1, true);  // lock to prevent MIDI override
      }
    }
  }

  // The count-off channel is percussion too; only channel 9 is a drum channel by
  // default, so it has to be told — and then LOCKED, or it loses the flag again on
  // the very first seek. Every `seq.currentTime = …` runs the sequencer's
  // setTimeToInternal, which starts with sendMIDIReset() → resetPreset(), and that
  // does `setDrums(channel % 16 === 9)`: channel 9 survives, channel 10 does not.
  // Measured 2026-09-25: isDrum was true after loading and false after the first
  // seek, so the count-off sounded as piano E5/F5 instead of wood blocks.
  // `setDrumFlag()` bails out on a locked preset, so the lock makes it stick
  // declaratively — no re-asserting after each seek, nothing to race against.
  // Order matters: set the flag first, then lock it in.
  synth.setDrums(COUNT_IN_CHANNEL, true);
  synth.lockController(COUNT_IN_CHANNEL, -1, true);

  // Apply 442 Hz tuning to instrument channels (not percussion)
  for (let ch = 0; ch < 16; ch++) {
    if (ch !== METRO_CHANNEL && ch !== COUNT_IN_CHANNEL) {
      synth.transposeChannel(ch, TUNING_SEMITONES);
    }
  }

  // Metronome mute state + volume (re-applied because a fresh song resets CCs).
  // The count-off channel stays unmuted on purpose, always.
  synth.muteChannel(METRO_CHANNEL, !checkMetronome.checked);
  synth.muteChannel(COUNT_IN_CHANNEL, false);
  applyMetronomeVolume();
}

// Set the metronome loudness live via MIDI CC 7 (channel volume), on both the
// metronome and the count-off channel so the count-off matches the clicks.
function applyMetronomeVolume() {
  if (!synth) return;
  synth.controllerChange(METRO_CHANNEL, CC_MAIN_VOLUME, metronomeVolume);
  synth.controllerChange(COUNT_IN_CHANNEL, CC_MAIN_VOLUME, metronomeVolume);
}

// Rebuild the sequence after a subdivision change, reusing the proven load path
// but PRESERVING the current piece, loop section and tempo. Playback is stopped;
// the user presses Play again (per design). A brief pause during rebuild is fine.
let metronomeReinitInProgress = false;
async function reinitMetronomeSubdivision() {
  if (!currentPiece || !currentMidiBuffer || !synth) return;
  if (metronomeReinitInProgress) return;
  metronomeReinitInProgress = true;
  stopPlayback();
  try {
    await loadSequenceFromBuffer(currentPiece, currentMidiBuffer, loopStart, loopEnd);
    setTempo(tempoBpm);   // re-apply current tempo to the fresh sequencer
    applyTrackMuting();   // re-apply current track + metronome mute state
    applyTrackVolumes();  // ditto for the per-voice levels (fresh song reset CC 7)
    measureDisplay.textContent = "Takt: —";
    buildBeatDots();      // rebuild the visual dot row for the new mode
  } catch (err) {
    console.error("Fehler beim Neuaufbau des Metronoms:", err);
    showError("Metronom konnte nicht aktualisiert werden.");
  } finally {
    metronomeReinitInProgress = false;
  }
}

async function selectPieceByIndex(index) {
  const piece = manifest.pieces[index];
  if (!piece) return;

  stopPlayback();
  currentPiece = piece;
  selectPiece.selectedIndex = index;

  // Load MIDI
  try {
    const res = await fetch(`/midi/${piece.file}`, { headers: authHeaders() });
    if (!res.ok) throw new Error(`HTTP ${res.status} beim Laden von ${piece.file}`);
    currentMidiBuffer = await res.arrayBuffer();

    await loadSequenceFromBuffer(piece, currentMidiBuffer);

    // Use startTempo from manifest if set, otherwise round MIDI tempo to nearest 5
    tempoBpm = piece.startTempo || Math.ceil(originalTempoBpm / 5) * 5;
    setTempo(tempoBpm);
    updateTempoDisplay();
  } catch (err) {
    console.error("Fehler beim Laden:", err);
    showError("Stück nicht verfügbar. Bitte wähle ein anderes Stück.");
    return;
  }

  // Update track checkboxes (must run before applyTrackVolumes — the sliders
  // it builds are where the levels live)
  updateTrackCheckboxes(piece.tracks);
  applyTrackVolumes();

  // Update loop range
  loopStart = 1;
  loopEnd = piece.totalMeasures;
  inputLoopStart.value = loopStart;
  inputLoopEnd.value = loopEnd;
  inputLoopStart.max = piece.totalMeasures;
  inputLoopEnd.max = piece.totalMeasures;
  totalMeasuresLabel.textContent = `(von ${piece.totalMeasures})`;

  measureDisplay.textContent = "Takt: —";

  buildBeatDots();

  logEvent("stueckwechsel");
}

// ─── Track Checkboxes ───────────────────────────────────
function updateTrackCheckboxes(tracks) {
  trackCheckboxes.innerHTML = "";
  for (const t of tracks) {
    const row = document.createElement("div");
    row.className = "track-row";

    const label = document.createElement("label");
    label.className = "checkbox-label";
    const cb = document.createElement("input");
    cb.type = "checkbox";
    cb.checked = true;
    cb.dataset.track = t.track;
    cb.addEventListener("change", () => onTrackToggle(cb));
    label.appendChild(cb);
    label.appendChild(document.createTextNode(t.name));

    // Per-voice loudness. Independent of the checkbox: muting a voice leaves
    // its level untouched. Session-only by design — a fresh piece (and a page
    // reload) starts every voice at DEFAULT_TRACK_VOLUME.
    const slider = document.createElement("input");
    slider.type = "range";
    slider.className = "track-volume";
    slider.min = 0;
    slider.max = 127;
    slider.value = DEFAULT_TRACK_VOLUME;
    slider.dataset.track = t.track;
    slider.setAttribute("aria-label", `Lautstärke ${t.name}`);

    row.appendChild(label);
    row.appendChild(slider);
    trackCheckboxes.appendChild(row);
  }
}

function onTrackToggle(cb) {
  const activeTracks = getActiveTracks();
  const metronomeOn = checkMetronome.checked;

  // Prevent silence: if this would turn off the last track and metronome is off, block it
  if (!cb.checked && activeTracks.length === 0 && !metronomeOn) {
    cb.checked = true;
    return;
  }

  applyTrackMuting();
}

function getActiveTracks() {
  const checkboxes = trackCheckboxes.querySelectorAll("input[type=checkbox]");
  return Array.from(checkboxes)
    .filter((cb) => cb.checked)
    .map((cb) => parseInt(cb.dataset.track, 10));
}

function applyTrackMuting() {
  if (!synth || !currentPiece) return;
  const active = new Set(getActiveTracks());
  for (const t of currentPiece.tracks) {
    const channel = t.track - 1; // MIDI channels are 0-based, manifest tracks are 1-based
    if (channel >= 0 && channel < 16) {
      synth.muteChannel(channel, !active.has(t.track));
    }
  }
  // Metronome is on MIDI channel 9 (percussion)
  synth.muteChannel(METRO_CHANNEL, !checkMetronome.checked);
}

// Push the per-voice slider positions to the synth as MIDI CC 7 (channel
// volume). Called when playback starts — NOT while a slider moves — so a level
// change takes effect on the next start, per design.
//
// The unlock → set → lock dance is required twice over: the synth drops a
// controllerChange for a locked controller (its own API included), and the MIDI
// file's own CC 7 at the start of the piece would otherwise reset every voice
// to 101. Locking CC 7 also survives the count-in seek and loop restarts,
// because the synth's controller reset skips locked controllers.
function applyTrackVolumes() {
  if (!synth || !currentPiece) return;
  const applied = [];
  for (const slider of trackCheckboxes.querySelectorAll("input.track-volume")) {
    const channel = parseInt(slider.dataset.track, 10) - 1; // manifest is 1-based
    if (channel < 0 || channel > 15) continue;
    const value = parseInt(slider.value, 10);
    if (!Number.isFinite(value)) continue;
    synth.lockController(channel, CC_MAIN_VOLUME, false);
    synth.controllerChange(channel, CC_MAIN_VOLUME, value);
    synth.lockController(channel, CC_MAIN_VOLUME, true);
    applied.push(`ch${channel}=${value}`);
  }
  debugLog(`Stimmen-Lautstärke: ${applied.join(" ") || "(keine)"}`);
}

// ─── Tempo ──────────────────────────────────────────────
function updateTempoDisplay() {
  tempoDisplay.textContent = `${tempoBpm} BPM`;
}

function setTempo(bpm) {
  tempoBpm = Math.max(25, Math.min(160, bpm));
  updateTempoDisplay();
  if (seq && originalTempoBpm > 0) {
    seq.playbackRate = tempoBpm / originalTempoBpm;
  }
}

// ─── Loop excerpt ───────────────────────────────────────
// Build a MIDI that IS the loop: a count-in measure, then measures
// `fromMeasure..toMeasure` of the piece, and nothing else. Where each part sits
// comes from timing.excerptLayout(); from the top, a pickup is played over the
// end of the count-in measure.
//
// This replaces seeking into the whole piece and stopping at a computed position.
// Two things follow from it, and both were bugs before:
//
//   - The loop START needs nothing muted. The count-in measure holds only
//     metronome clicks because it is the only thing at tick 0.
//   - The loop END needs no decision at all. End-of-track IS the loop end, so the
//     stray click from stopping a hair too late cannot happen, at any tempo on
//     any device. That was the last main-thread timing call in the audio path.
//
// Measure boundaries come from the measure TABLE, not from arithmetic, so a piece
// that changes time signature (Winter Wonderland 4/4 → 2/2, the medley 4/4 → 3/4
// after a pickup) cuts on the right barlines and gets the right number of clicks
// per bar.
function buildLoopMidi(midiBuffer, table, layout, fromMeasure, toMeasure) {
  const src = new Uint8Array(midiBuffer);
  const division = table.division;
  const subClicks = getSubClicks(metronomeSubdivision);

  // The count-off is in the meter of the measure the loop starts on: counting a
  // 2/2 bar in with four clicks would be counting the wrong thing.
  const { first, fromTick, toTick, musicStart, endTick } = layout;

  // --- Original tracks, cut to the loop and moved behind the count-in ---
  const cutTracks = [];
  let offset = 14;
  const origTrackCount = (src[10] << 8) | src[11];
  for (let t = 0; t < origTrackCount; t++) {
    while (offset < src.length - 4 && !(src[offset]===0x4D && src[offset+1]===0x54 && src[offset+2]===0x72 && src[offset+3]===0x6B)) offset++;
    const trackLen = (src[offset+4]<<24)|(src[offset+5]<<16)|(src[offset+6]<<8)|src[offset+7];
    const trackStart = offset + 8;
    const trackEnd = trackStart + trackLen;
    cutTracks.push(timing.extractTrackRange(src, trackStart, trackEnd, fromTick, toTick, musicStart));
    offset = trackEnd;
  }

  // --- Metronome track: count-in, then one entry per loop measure ---
  const metroEvents = [];
  let currentTick = 0;

  const nameBytes = new TextEncoder().encode("Metronom");
  metroEvents.push(0x00, 0xFF, 0x03, nameBytes.length, ...nameBytes);

  const microsecondsPerBeat = Math.round(60000000 / parseMidiTempo(midiBuffer));
  metroEvents.push(0x00, 0xFF, 0x51, 0x03,
    (microsecondsPerBeat >> 16) & 0xFF,
    (microsecondsPerBeat >> 8) & 0xFF,
    microsecondsPerBeat & 0xFF);

  const emitClick = (absTick, note, vel, channel) => {
    const delta = Math.round(absTick - currentTick);
    metroEvents.push(...timing.writeVarLen(delta));
    metroEvents.push(0x90 | channel, note, vel);
    currentTick += delta;
    metroEvents.push(0x01);
    metroEvents.push(0x80 | channel, note, 0x00);
    currentTick += 1;
  };

  // The count-in clicks the same subdivision as the loop, so a slow piece written
  // in sixteenths is counted in in sixteenths and the beat dots match what is
  // heard. It sits on the always-audible channel, so the metronome checkbox
  // cannot silence it.
  const clickMeasure = (startTick, measure, channel) => {
    const ticksPerBeat = measure.ticks / measure.beats;
    for (let beat = 0; beat < measure.beats; beat++) {
      const beatTick = startTick + beat * ticksPerBeat;
      emitClick(
        beatTick,
        beat === 0 ? METRO_DOWNBEAT : METRO_BEAT,
        beat === 0 ? METRO_DOWNBEAT_VELOCITY : METRO_BEAT_VELOCITY,
        channel,
      );
      for (const sub of subClicks) emitClick(beatTick + sub.frac * ticksPerBeat, sub.note, sub.vel, channel);
    }
  };

  // A pickup lies under the count-in measure, so its clicks are the count-in's.
  clickMeasure(0, first, COUNT_IN_CHANNEL);
  for (const measure of table.measures) {
    if (measure.number < fromMeasure || measure.number > toMeasure || measure.pickup) continue;
    clickMeasure(measure.startTick - fromTick + musicStart, measure, METRO_CHANNEL);
  }

  metroEvents.push(...timing.writeVarLen(Math.max(0, endTick - currentTick)), 0xFF, 0x2F, 0x00);

  const withHeader = (data) => {
    const header = new Uint8Array(8);
    header[0]=0x4D; header[1]=0x54; header[2]=0x72; header[3]=0x6B;
    header[4] = (data.length >> 24) & 0xFF;
    header[5] = (data.length >> 16) & 0xFF;
    header[6] = (data.length >> 8) & 0xFF;
    header[7] = data.length & 0xFF;
    return { header, data };
  };

  const parts = [...cutTracks, new Uint8Array(metroEvents)].map(withHeader);
  let totalSize = 14;
  for (const part of parts) totalSize += part.header.length + part.data.length;

  const result = new Uint8Array(totalSize);
  result.set(src.slice(0, 14), 0);
  const trackCount = parts.length;
  result[10] = (trackCount >> 8) & 0xFF;
  result[11] = trackCount & 0xFF;
  result[12] = (division >> 8) & 0xFF;
  result[13] = division & 0xFF;

  let writePos = 14;
  for (const part of parts) {
    result.set(part.header, writePos); writePos += part.header.length;
    result.set(part.data, writePos); writePos += part.data.length;
  }
  return result.buffer;
}

// ─── Parse MIDI tempo from binary (reliable, no async) ──
function parseMidiTempo(midiBuffer) {
  return timing.parseMidiTempo(midiBuffer);
}

// ─── Measure ↔ Time Conversion ──────────────────────────
// The sequencer now holds ONLY the loop (buildLoopMidi), so its timeline is:
// [0, countInTicks) the count-off, then measures loopStart..loopEnd, then end of
// track. A pickup from the top overlaps the end of the count-off, which is why
// piece ticks map through `musicStart`, not `countInTicks`. Measure numbers stay
// the ones on the player's sheet music; these two map them into that timeline.
function parseTimeSignature() {
  return timing.parseTimeSignature(currentPiece?.timeSignature);
}

function secondsPerTick() {
  return timing.secondsPerTick(measureTable?.division || 384, originalTempoBpm);
}

// Start of a piece measure in the loaded excerpt, in MIDI seconds.
// `measure` may be loopEnd + 1, meaning the barline that closes the loop.
function measureToSeconds(measure) {
  if (!measureTable || !loopBuild) return 0;
  const tick = timing.measureStartTick(measureTable, measure) - loopBuild.fromTick + loopBuild.musicStart;
  return tick * secondsPerTick();
}

// The measure a position in the excerpt falls in, with its meter. Covers the
// count-off, which carries the meter of the measure the loop starts on.
// Used by the beat dots, which have to follow a meter change like the metronome.
function excerptMeasureAt(seconds) {
  if (!measureTable || !loopBuild) return null;
  const perTick = secondsPerTick();
  const tick = seconds / perTick;
  if (tick < loopBuild.countInTicks) {
    const first = measureTable.measures.find((m) => m.number === loopBuild.fromMeasure);
    return { beats: first?.beats || 4, startSeconds: 0, seconds: loopBuild.countInTicks * perTick };
  }
  const pieceTick = tick - loopBuild.musicStart + loopBuild.fromTick;
  let found = null;
  for (const m of measureTable.measures) {
    if (pieceTick < m.startTick) break;
    found = m;
  }
  if (!found) return null;
  return {
    beats: found.beats,
    startSeconds: (found.startTick - loopBuild.fromTick + loopBuild.musicStart) * perTick,
    seconds: found.ticks * perTick,
  };
}

// Which piece measure a position in the excerpt falls in. During the count-off
// this is loopStart - 1 conceptually; callers clamp.
function secondsToMeasure(seconds) {
  if (!measureTable || !loopBuild) return 1;
  const tick = seconds / secondsPerTick() + loopBuild.fromTick - loopBuild.musicStart;
  return timing.measureAtTick(measureTable, tick);
}

// ─── Playback ───────────────────────────────────────────
async function startPlayback() {
  if (!seq || !currentPiece || isPlaying || startInProgress) return;
  playRequested = true;

  // iOS requires resume() directly in the user gesture handler, not after an await
  audioCtx.resume();

  // Validate loop bounds (swap if start > end)
  let rawStart = parseInt(inputLoopStart.value, 10) || 1;
  let rawEnd = parseInt(inputLoopEnd.value, 10) || currentPiece.totalMeasures;
  if (rawStart > rawEnd) [rawStart, rawEnd] = [rawEnd, rawStart];
  loopStart = Math.max(1, Math.min(rawStart, currentPiece.totalMeasures));
  loopEnd = Math.max(loopStart, Math.min(rawEnd, currentPiece.totalMeasures));
  inputLoopStart.value = loopStart;
  inputLoopEnd.value = loopEnd;

  // The sequencer holds the loop and nothing else, so a moved loop range needs a
  // fresh excerpt. Once per Play press, never once per repetition.
  if (!loopBuild || loopBuild.fromMeasure !== loopStart || loopBuild.toMeasure !== loopEnd) {
    startInProgress = true;
    try {
      await loadSequenceFromBuffer(currentPiece, currentMidiBuffer, loopStart, loopEnd);
    } catch (err) {
      startInProgress = false;
      console.error("Fehler beim Aufbau des Loops:", err);
      showError("Loop konnte nicht vorbereitet werden. Bitte Seite neu laden.");
      return;
    }
    startInProgress = false;
    if (!playRequested) return; // Stop pressed while the excerpt was building
  }

  applyTrackMuting();
  applyTrackVolumes();  // slider changes take effect here, on the next start
  setTempo(tempoBpm);

  isPlaying = true;
  logEvent("play");

  // Visual dots animate during count-in and playback (decoupled from audio)
  startBeatDotAnimation();

  startLoopIteration();
}

function stopPlayback() {
  playRequested = false;
  if (!seq) return;
  isPlaying = false;
  if (loopRestartTimeout) {
    clearTimeout(loopRestartTimeout);
    loopRestartTimeout = null;
  }
  seq.pause();
  stopPositionPolling();
  stopBeatDotAnimation();
  measureDisplay.textContent = "Takt: —";
}

// Start one pass of the loop.
//
// There is nothing to get right here any more, which is the point. The excerpt
// begins with the count-off and ends at the loop's closing barline, so: seek to
// 0, play. Seeking to 0 is also the one seek position that cannot lose its first
// event to float drift — time 0 takes the sequencer's integer tick path
// (see the v1.0.7 note in CLAUDE.md), so COUNT_IN_SEEK_MARGIN is gone with it.
function startLoopIteration() {
  audioCtx.resume();
  seq.currentTime = 0;
  seq.play();
  startPositionPolling();
}

// Restart after the sequencer reports it reached the end of the excerpt. The
// all-notes-off at the closing barline is the last voice event in the file, so
// "song ended" IS "loop ended" — no polled position, no margin, no stray click.
function onExcerptEnded() {
  if (!isPlaying) return;
  stopPositionPolling();
  logEvent("loop-repeat");
  loopRestartTimeout = setTimeout(() => {
    loopRestartTimeout = null;
    if (isPlaying) startLoopIteration();
  }, LOOP_RESTART_PAUSE_MS);
}

// Purely cosmetic now: it drives the measure readout and nothing else. Nothing
// audible depends on when it fires or on how accurate seq.currentTime is.
function startPositionPolling() {
  stopPositionPolling();
  let shown = null;
  positionPollInterval = setInterval(() => {
    if (!seq || !isPlaying) return;
    const measure = Math.min(Math.max(secondsToMeasure(seq.currentTime), loopStart), loopEnd);
    if (measure !== shown) {
      shown = measure;
      measureDisplay.textContent = `Takt: ${measure} / ${loopEnd}`;
    }
  }, timing.LOOP_POLL_SECONDS * 1000);
}

function stopPositionPolling() {
  if (positionPollInterval) {
    clearInterval(positionPollInterval);
    positionPollInterval = null;
  }
}

// ─── Visual beat dots ───────────────────────────────────
// A row of dots reflecting the current subdivision: main beats + subdivisions.
// Size/colour encode the accent structure; the active dot flashes. Driven by
// requestAnimationFrame reading the sequencer position — fully DECOUPLED from
// the audio timeline, so it never affects playback precision.
let beatDotEls = [];        // flat list of dot elements, index = beat*slots + slot
let beatDotBeats = 0;
let beatDotSlotFracs = [0]; // fractions within a beat, slot 0 is the beat itself
let beatDotRaf = null;
let lastActiveDotIndex = -1;

// `beats` defaults to the piece's opening meter; updateBeatDots rebuilds the row
// when the music moves into a measure with a different one.
function buildBeatDots(beats) {
  if (!beatDots) return;
  beatDots.innerHTML = "";
  beatDotEls = [];
  lastActiveDotIndex = -1;
  if (!currentPiece) { beatDotBeats = 0; return; }

  const subs = getSubClicks(metronomeSubdivision);
  beatDotBeats = beats || measureTable?.measures?.[0]?.beats || parseTimeSignature().beats;
  beatDotSlotFracs = [0, ...subs.map((s) => s.frac)];

  for (let beat = 0; beat < beatDotBeats; beat++) {
    for (let slot = 0; slot < beatDotSlotFracs.length; slot++) {
      const dot = document.createElement("div");
      const kind = slot === 0 ? (beat === 0 ? "downbeat" : "beat") : "sub";
      dot.className = `beat-dot ${kind}`;
      beatDots.appendChild(dot);
      beatDotEls.push(dot);
    }
  }
}

function clearActiveDot() {
  if (lastActiveDotIndex >= 0 && beatDotEls[lastActiveDotIndex]) {
    beatDotEls[lastActiveDotIndex].classList.remove("active");
  }
  lastActiveDotIndex = -1;
}

function updateBeatDots() {
  if (!seq || !isPlaying || !beatDotEls.length || beatDotBeats === 0) return;

  const measure = excerptMeasureAt(seq.currentTime);
  if (!measure || measure.seconds <= 0) return;
  // A meter change mid-loop changes how many dots there are.
  if (measure.beats !== beatDotBeats) buildBeatDots(measure.beats);

  const posInMeasure = Math.min(Math.max((seq.currentTime - measure.startSeconds) / measure.seconds, 0), 0.9999);
  const beatFloat = posInMeasure * beatDotBeats;
  const beatIndex = Math.min(Math.floor(beatFloat), beatDotBeats - 1);
  const fracInBeat = beatFloat - beatIndex;

  // Which slot within the beat: the last slot whose start fraction we've passed.
  let slot = 0;
  for (let i = 0; i < beatDotSlotFracs.length; i++) {
    if (fracInBeat + 1e-4 >= beatDotSlotFracs[i]) slot = i;
  }

  const activeIndex = beatIndex * beatDotSlotFracs.length + slot;
  if (activeIndex !== lastActiveDotIndex) {
    clearActiveDot();
    const el = beatDotEls[activeIndex];
    if (el) el.classList.add("active");
    lastActiveDotIndex = activeIndex;
  }
}

function startBeatDotAnimation() {
  stopBeatDotAnimation();
  const tick = () => {
    updateBeatDots();
    beatDotRaf = requestAnimationFrame(tick);
  };
  beatDotRaf = requestAnimationFrame(tick);
}

function stopBeatDotAnimation() {
  if (beatDotRaf) {
    cancelAnimationFrame(beatDotRaf);
    beatDotRaf = null;
  }
  clearActiveDot();
}

// ─── Error Display ──────────────────────────────────────
function showError(msg) {
  errorMessage.textContent = msg;
  errorMessage.classList.remove("hidden");
  setTimeout(() => errorMessage.classList.add("hidden"), 5000);
}

// ─── Event Handlers ─────────────────────────────────────

// Login: combined audio init + password check (single user gesture for iOS)
btnLogin.addEventListener("click", async () => {
  const pw = inputPassword.value;
  localStorage.setItem(PASSWORD_KEY, pw);

  if (!(await testAuth())) {
    passwordError.classList.remove("hidden");
    return;
  }
  passwordError.classList.add("hidden");

  try {
    await initAudio();
  } catch (err) {
    showError("Audio konnte nicht initialisiert werden.");
    return;
  }

  await enterPlayer();
});

inputPassword.addEventListener("keydown", (e) => {
  if (e.key === "Enter") btnLogin.click();
});

// Auto-login if password saved from previous visit
(async () => {
  const savedPassword = getPassword();
  if (savedPassword && (await testAuth())) {
    inputPassword.value = savedPassword;
  }
})();

// Piece selection
selectPiece.addEventListener("change", () => {
  const index = selectPiece.selectedIndex;
  selectPieceByIndex(index);
});

// All tracks on
btnAllOn.addEventListener("click", () => {
  trackCheckboxes.querySelectorAll("input[type=checkbox]").forEach((cb) => {
    cb.checked = true;
  });
  applyTrackMuting();
});

// Metronome toggle: prevent silence
checkMetronome.addEventListener("change", () => {
  const activeTracks = getActiveTracks();
  if (!checkMetronome.checked && activeTracks.length === 0) {
    checkMetronome.checked = true;
    return;
  }
  // Mute/unmute the MIDI metronome channel
  if (synth) {
    synth.muteChannel(METRO_CHANNEL, !checkMetronome.checked);
  }
});

// Metronome settings: gear toggles the subdivision popover
btnMetronomeSettings.addEventListener("click", () => {
  const open = metronomeSettings.classList.toggle("hidden") === false;
  btnMetronomeSettings.setAttribute("aria-expanded", String(open));
});

// Subdivision change: rebuild the MIDI metronome, preserving piece/loop/tempo.
// Playback stops; the user presses Play again (per design).
metronomeSettings.querySelectorAll('input[name="subdivision"]').forEach((radio) => {
  radio.addEventListener("change", () => {
    if (!radio.checked) return;
    metronomeSubdivision = radio.value;
    logEvent(`unterteilung-${radio.value}`);
    reinitMetronomeSubdivision();
  });
});

// Metronome volume: live CC 7 on the percussion channel, persisted across sessions.
inputMetronomeVolume.value = metronomeVolume;
inputMetronomeVolume.addEventListener("input", () => {
  metronomeVolume = parseInt(inputMetronomeVolume.value, 10);
  localStorage.setItem(METRO_VOLUME_KEY, String(metronomeVolume));
  applyMetronomeVolume();
});

// Loop inputs
inputLoopStart.addEventListener("change", () => {
  logEvent("loop-aenderung");
});
inputLoopEnd.addEventListener("change", () => {
  logEvent("loop-aenderung");
});

// Tempo
btnTempoDown.addEventListener("click", () => {
  setTempo(tempoBpm - 5);
  logEvent("tempo-aenderung");
});
btnTempoUp.addEventListener("click", () => {
  setTempo(tempoBpm + 5);
  logEvent("tempo-aenderung");
});

// Play
btnPlay.addEventListener("click", () => {
  if (isPlaying) {
    stopPlayback();
    logEvent("stop");
  }
  startPlayback();
});

// Stop
btnStop.addEventListener("click", () => {
  stopPlayback();
  logEvent("stop");
});

// ─── Version Display ────────────────────────────────────
fetch("/version")
  .then((r) => r.json())
  .then((data) => {
    if (data.version) {
      document.querySelectorAll("#version-display, #version-display-login").forEach((el) => {
        el.textContent = `v${data.version}`;
      });
    }
  })
  .catch(() => {});

// ─── Was ist neu ────────────────────────────────────────
// The info button and the version number in both footers open the dialog.
// A click on the backdrop closes it, like the close button. The backdrop counts
// as the dialog element itself, and so does the dialog's padding — hence the
// check against its box rather than against e.target alone.
const whatsNew = $("whats-new");
document.querySelectorAll("[data-whats-new]").forEach((el) => {
  el.addEventListener("click", () => whatsNew.showModal());
});
whatsNew.addEventListener("click", (e) => {
  if (e.target !== whatsNew) return;
  const box = whatsNew.getBoundingClientRect();
  const inside = e.clientX >= box.left && e.clientX <= box.right && e.clientY >= box.top && e.clientY <= box.bottom;
  if (!inside) whatsNew.close();
});

// ─── Service Worker Registration ────────────────────────
if ("serviceWorker" in navigator) {
  navigator.serviceWorker.register("/sw.js").catch(() => {});
}
